import { useContext, useEffect, useState } from "react";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import { AlertModalCheckIcon } from "../../../../assets";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AppLayout } from "../../../../containers/layout/app";
import { EditAssignTellerWrapper } from "./styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseButton } from "../../../../components/button/styled";
import Cookies from "universal-cookie";
import { AppContext } from "../../../../context/appContext";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllChartOfAccountService } from "../../../../utils/api/accounting/chartofaccount/retrieveAllChartOfAccount";
import { retrieveAllMFIUsersService } from "../../../../utils/api/usermanagement/mfiuser/retrieveAllMFIUsers";
import { useNavigate, useParams } from "react-router-dom";
import { retrieveTellerService } from "../../../../utils/api/usermanagement/assignteller/retrieveTeller";
import { updateTellerService } from "../../../../utils/api/usermanagement/assignteller/updateTeller";
import { retrieveMFIUserService } from "../../../../utils/api/usermanagement/mfiuser/retrieveMFIUser";
import { retrieveBranchService } from "../../../../utils/api/configuration/branch/retrieveBranch";

export const EditAssignTeller = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { id = "" } = useParams();
    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [formDetails, setFormDetails] = useState({
        tillNumber: " ",
        tellerId: " ",
        postingLimit: "",
        branch: "",
    });
    const [institutionId, setInstitutionId] = useState<string | null>(null);

    useEffect(() => {
        setActiveNavItem("User Management");
    }, [setActiveNavItem]);

    const { data: glCodes } = useQuery({
        queryKey: ['glCodes', TOKEN],
        queryFn: async () => {
            const chartOfAccounts = await retrieveAllChartOfAccountService(TOKEN);
            const glCodes = chartOfAccounts?.data?.filter((chartOfAccount: Record<string, any>) => chartOfAccount.glAccountType === "GL Account" && chartOfAccount.glType === "100")?.map((chartOfAccount: Record<string, any>) => chartOfAccount.glCode);
            return glCodes;
        },
        enabled: !!TOKEN,
    });

    const { data: tellers } = useQuery({
        queryKey: ['tellers', TOKEN],
        queryFn: async () => {
            const response = await retrieveAllMFIUsersService(TOKEN);
            return response.data;
        },
        enabled: !!TOKEN,
    });

    useEffect(() => {
        if (!id) return;
        retrieveTellerService(TOKEN, id)
            .then(async (data) => {
                setInstitutionId(data.institution);
                return setFormDetails({
                    tillNumber: data?.tillNumber,
                    tellerId: data?.id,
                    postingLimit: data?.postingLimit,
                    branch: data?.branch,
                });
            })
            .catch((err) => {
                console.error("Failed to fetch assign teller:", err);
            });
    }, [TOKEN, id]);

    useEffect(() => {
        const fetchMFIUserAndBranch = async () => {
            if (!formDetails?.tellerId.trim()) return;
            try {
                const mfiUser = await retrieveMFIUserService(TOKEN, formDetails.tellerId);
                try {
                    const branchData = await retrieveBranchService(TOKEN, mfiUser.branch);
                    setFormDetails((prev) => ({
                        ...prev,
                        branch: branchData.name
                    }));
                } catch (branchError) {
                    console.error("Failed to fetch branch:", branchError);
                }
            } catch (userError) {
                console.error("Failed to fetch MFI user:", userError);
            }
        };
        fetchMFIUserAndBranch();
    }, [TOKEN, formDetails.tellerId]);

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            branch: (name === "tellerId") ? "" : prev.branch,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        if (!institutionId) return;
        try {
            const response = await updateTellerService(TOKEN, institutionId, id, formDetails);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to update assign teller. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to update assign teller. ${error.message}`);
            console.error('Failed to update assign teller:', error);
        }
    };

    return (
        <AppLayout
            pageId={"Assign-Teller-Management"}
            pageTitle={"Assign Teller"}
        >
            <EditAssignTellerWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Assign Teller Updated Successfully"
                    message="Your information has been successfully updated."
                    callToAction="Back to Tellers List"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required information to update assign teller
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Till Number
                                </BaseLabel>
                                <BaseSelect
                                    name="tillNumber"
                                    fontweight={400}
                                    value={formDetails.tillNumber}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value=" "
                                    >
                                        Select Till Number
                                    </BaseOption>
                                    {glCodes?.map((glCode: string, index: number) => (
                                        <BaseOption
                                            key={index}
                                            value={glCode}
                                        >
                                            {glCode}
                                        </BaseOption>
                                    ))}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Teller
                                </BaseLabel>
                                <BaseSelect
                                    name="tellerId"
                                    fontweight={400}
                                    value={formDetails.tellerId}
                                    colour="var(--semi-gray-color)"
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value=" "
                                    >
                                        Select Teller
                                    </BaseOption>
                                    {tellers?.map((teller: Record<string, any>, index: number) => (
                                        <BaseOption
                                            key={index}
                                            value={teller?.id}
                                        >
                                            {`${teller?.firstName} ${teller?.lastName}`}
                                        </BaseOption>
                                    ))}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Posting Limit
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="postingLimit"
                                    value={formDetails.postingLimit}
                                    placeholder="Enter Limit"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, tablet: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Branch
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="branch"
                                    readOnly
                                    value={formDetails.branch}
                                    placeholder="Enter Branch"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </EditAssignTellerWrapper>
        </AppLayout>
    )
}